import React, { useState, useEffect } from 'react';
import { Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Select, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2} from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';

export default function SupplierProductsModal({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [form, setForm] = useState({ product_code: '', name: '', description: '', unit_price: '', vat_rate_id: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const searchCompanies = async (q) => {
    if (q.length < 1) { setCompanies([]); return; }
    try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}&limit=15`); setCompanies(data); } catch(e) {}
  };
  const fetchProducts = async (companyId) => {
    try { const { data } = await api.get(`/supplier-products?company_id=${companyId}`); setProducts(data); } catch(e) { setProducts([]); }
  };
  const fetchVatRates = async () => { try { const { data } = await api.get('/vat-rates'); setVatRates(data); } catch(e) {} };

  useEffect(() => { if (active) { fetchVatRates(); setSelectedCompany(null); setProducts([]); setShowForm(false); } }, [isOpen]);

  const selectCompany = (c) => { setSelectedCompany(c); fetchProducts(c.id); setShowForm(false); };

  const handleSave = async () => {
    if (!form.name || !selectedCompany) return;
    setSaving(true);
    try {
      const payload = { ...form, company_id: selectedCompany.id, unit_price: parseFloat(form.unit_price) || 0, vat_rate_id: form.vat_rate_id || null };
      if (editingId) await api.put(`/supplier-products/${editingId}`, payload);
      else await api.post('/supplier-products', payload);
      await fetchProducts(selectedCompany.id); setShowForm(false); setEditingId(null);
      setForm({ product_code: '', name: '', description: '', unit_price: '', vat_rate_id: '' });
      toast({ title: editingId ? 'Updated' : 'Created', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const openEdit = (p) => {
    setForm({ product_code: p.product_code || '', name: p.name, description: p.description || '', unit_price: p.unit_price || '', vat_rate_id: p.vat_rate_id || '' });
    setEditingId(p.id); setShowForm(true);
  };

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Supplier Products &amp; Services">
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Search Company</FormLabel>
              <Input size="sm" placeholder="Type company name..." value={companySearch}
                onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }} />
              {companies.length > 0 && !selectedCompany && (
                <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="150px" overflowY="auto">
                  {companies.map(c => (
                    <Box key={c.id} px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                      onClick={() => { selectCompany(c); setCompanySearch(c.name); setCompanies([]); }}>
                      {c.name} {c.company_code ? `(${c.company_code})` : ''}
                    </Box>
                  ))}
                </Box>
              )}
            </FormControl>

            {selectedCompany && (
              <>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Badge colorScheme="blue" fontSize="11px">{selectedCompany.name}</Badge>
                    <Button size="xs" variant="ghost" onClick={() => { setSelectedCompany(null); setProducts([]); setCompanySearch(''); }}>Change</Button>
                  </HStack>
                  <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant={showForm ? 'solid' : 'outline'}
                    onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ product_code: '', name: '', description: '', unit_price: '', vat_rate_id: '' }); }}>
                    {showForm ? 'Cancel' : 'Add Product'}
                  </Button>
                </Flex>

                {showForm && (
                  <Box bg="teal.50" p={3} borderRadius="md">
                    <VStack spacing={2}>
                      <HStack w="100%" spacing={2}>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Product Code</FormLabel>
                          <Input size="sm" value={form.product_code} onChange={e => setForm(p => ({ ...p, product_code: e.target.value }))} /></FormControl>
                        <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                          <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
                      </HStack>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                        <Textarea size="sm" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
                      <HStack w="100%" spacing={2}>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Unit Price (£)</FormLabel>
                          <Input size="sm" type="number" step="0.01" value={form.unit_price} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} /></FormControl>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>VAT Rate</FormLabel>
                          <Select size="sm" value={form.vat_rate_id} onChange={e => setForm(p => ({ ...p, vat_rate_id: e.target.value }))}>
                            <option value="">No VAT</option>
                            {vatRates.map(v => <option key={v.id} value={v.id}>{v.name} ({v.rate}%)</option>)}
                          </Select></FormControl>
                      </HStack>
                      <Button size="xs" colorScheme="teal" onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add'}</Button>
                    </VStack>
                  </Box>
                )}

                {products.length > 0 ? (
                  <Table size="sm"><Thead><Tr>
                    <Th px={2}>Code</Th><Th px={2}>Name</Th><Th px={2}>Description</Th><Th px={2} isNumeric>Price</Th><Th px={2}>VAT</Th><Th px={2}></Th>
                  </Tr></Thead><Tbody>
                    {products.map(p => (
                      <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                        <Td px={2} fontSize="11px">{p.product_code || '—'}</Td>
                        <Td px={2} fontSize="11px" fontWeight="500">{p.name}</Td>
                        <Td px={2} fontSize="10px" color="gray.500" maxW="200px" isTruncated>{p.description || '—'}</Td>
                        <Td px={2} fontSize="11px" isNumeric>£{parseFloat(p.unit_price || 0).toFixed(2)}</Td>
                        <Td px={2} fontSize="10px">{p.vat_rate_name || '—'}</Td>
                        <Td px={2}>
                          <HStack spacing={0}>
                            <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(p)} aria-label="Edit" />
                            <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                              onClick={async () => { await api.delete(`/supplier-products/${p.id}`); fetchProducts(selectedCompany.id); }} aria-label="Delete" />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody></Table>
                ) : <Text fontSize="11px" color="gray.400" textAlign="center" py={4}>No products for this company</Text>}
              </>
            )}
          </VStack>
    </PageShell>
  );
}
